import type { Ref } from 'vue'
import { computed, ref, shallowRef, unref, watch } from 'vue'

import { container, TYPES } from '@/libs/di/inversify.config'
import type { MessageUsecase } from '@/apis/usecases'
import type { Normalized } from '@/libs/utilities/normalize'
import {
  createNormalizedEmpty,
  denormalize,
  normalize,
} from '@/libs/utilities/normalize'

import { useUpdateLatestReadMessage } from '@/hooks/useUpdateLatestReadMessage'
import { isClientError } from '@/libs/utilities/CustomErrors'
import type { AnyMessage, Match, Pagination } from '@/types/app'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { useLogger } from '@/logger/logger'

type NormalizedMessages = Normalized<AnyMessage, 'id'>

const normalizeAndFilterMessages = (
  currentStore: NormalizedMessages,
  messages: readonly AnyMessage[]
): NormalizedMessages => {
  const { entities, values } = normalize<AnyMessage, 'id'>(messages, 'id')
  const filteredValues = values.filter(id => !currentStore.entities.has(id))
  return { entities, values: filteredValues }
}

export const useFetchMessages = (
  matchRef: Ref<Maybe<Match>>,
  numberOfMessageOnAFetch: number,
  isDisabledToScrollToBottomRef: Ref<boolean>,
  entranceGUID: string
) => {
  const messageStore = shallowRef<NormalizedMessages>(
    createNormalizedEmpty<AnyMessage, 'id'>()
  )

  const logger = useLogger()

  const addMessages = (
    messages: ReadonlyArray<AnyMessage>,
    mode: 'append' | 'prepend'
  ): void => {
    const rawStore = unref(messageStore)
    const { entities, values } = normalizeAndFilterMessages(rawStore, messages)
    messageStore.value = {
      entities: new Map([...rawStore.entities, ...entities]),
      values:
        mode === 'append'
          ? [...rawStore.values, ...values]
          : [...values, ...rawStore.values],
    }
  }

  const hasNextPage = ref(true)
  const hasPrevPage = ref(true)

  const nextMessagesRequestParameters = computed((): Pagination => {
    const { values, entities } = unref(messageStore)
    if (values.length === 0) {
      return { limit: numberOfMessageOnAFetch }
    }
    const lastId = values[values.length - 1]
    const since = entities.get(lastId)?.createdAt ?? undefined
    return {
      since,
      limit: numberOfMessageOnAFetch,
    }
  })

  const previousMessagesRequestParameters = computed((): Pagination => {
    const { values, entities } = unref(messageStore)
    if (values.length === 0) {
      return { limit: 0 }
    }
    const until = entities.get(values[0])?.createdAt ?? undefined
    return {
      until,
      limit: numberOfMessageOnAFetch,
    }
  })

  const messages = computed((): readonly AnyMessage[] => {
    const { values, entities } = unref(messageStore)
    return denormalize<AnyMessage, 'id'>(values, entities)
  })

  const { updateLatestReadMessage } = useUpdateLatestReadMessage()

  const loading = ref(false)

  const fetchNextMessages = async (force?: boolean): Promise<void> => {
    if (matchRef.value === undefined) {
      return
    }

    if (!force && !hasNextPage.value) {
      return
    }

    loading.value = true

    try {
      const roomId = matchRef.value.roomId

      const messageUsecase = container.get<MessageUsecase>(TYPES.MessageUsecase)
      const { data, pageInfo } = await messageUsecase.getRoomMessages(
        roomId,
        entranceGUID,
        nextMessagesRequestParameters.value
      )

      addMessages(data, 'append')
      isDisabledToScrollToBottomRef.value = false
      hasNextPage.value = pageInfo.hasNextPage

      const [latestMessage] = data.slice(-1)
      if (latestMessage === undefined) {
        return
      }

      updateLatestReadMessage(roomId, latestMessage.guid, entranceGUID)
    } finally {
      loading.value = false
    }
  }

  const fetchPreviousMessages = async (): Promise<void> => {
    if (
      !hasPrevPage.value ||
      messages.value.length === 0 ||
      matchRef.value === undefined
    ) {
      return
    }

    loading.value = true

    try {
      const roomId = matchRef.value.roomId

      const messageUsecase = container.get<MessageUsecase>(TYPES.MessageUsecase)
      const { data, pageInfo } = await messageUsecase.getRoomMessages(
        roomId,
        entranceGUID,
        previousMessagesRequestParameters.value
      )

      addMessages(data, 'prepend')
      isDisabledToScrollToBottomRef.value = true
      hasPrevPage.value = pageInfo.hasNextPage

      await logger.ltlPeople.event.loadPastMessages(logger.buildMeta(), {
        data: {
          roomId: roomId.toString(),
        },
      })
    } finally {
      loading.value = false
    }
  }

  // match watcher
  watch(
    () => matchRef.value?.id,
    async matchId => {
      if (matchId == null) {
        return
      }
      isDisabledToScrollToBottomRef.value = false
      messageStore.value = createNormalizedEmpty<AnyMessage, 'id'>()
      hasNextPage.value = true
      hasPrevPage.value = true

      try {
        await fetchNextMessages()
      } catch (error) {
        if (!isClientError(error)) {
          newrelicCaptureException(error)
        }
      }
    },
    { immediate: true }
  )

  const appendMessage = (message: AnyMessage) => {
    addMessages([message], 'append')
  }

  return {
    loading,
    messages,
    fetchPreviousMessages,
    fetchNextMessages,
    appendMessage,
    messageStore,
  }
}
